import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class AppUserReviewService {
  constructor() {
    this.reviewsTable = 'app_user_reviews';
    this.appsTable = 'apps';
    this.installationsTable = 'app_installations';
  }

  // Submit a user review for an app
  async submitReview(userId, appId, reviewData, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const {
        rating,
        review_text,
        helpful_count = 0,
        is_verified_user = false
      } = reviewData;

      // Validate rating
      if (rating < 1 || rating > 5) {
        throw new ServiceError('INVALID_RATING', 'Rating must be between 1 and 5');
      }

      // Check if user has installed the app
      const { data: installation } = await supabase
        .from(this.installationsTable)
        .select('id')
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('tenant_id', tenantUuid)
        .single();

      if (!installation) {
        throw new ServiceError('APP_NOT_INSTALLED', 'You must install the app before reviewing it');
      }

      // Check if user already reviewed this app
      const { data: existingReview } = await supabase
        .from(this.reviewsTable)
        .select('id')
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('tenant_id', tenantUuid)
        .single();

      let result;
      if (existingReview) {
        // Update existing review
        result = await supabase
          .from(this.reviewsTable)
          .update({
            rating,
            review_text,
            helpful_count,
            is_verified_user,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingReview.id)
          .select()
          .single();
      } else {
        // Create new review
        result = await supabase
          .from(this.reviewsTable)
          .insert({
            user_id: userId,
            app_id: appId,
            tenant_id: tenantUuid,
            rating,
            review_text,
            helpful_count,
            is_verified_user,
            review_date: new Date().toISOString()
          })
          .select()
          .single();
      }

      if (result.error) {
        throw new ServiceError('REVIEW_SUBMISSION_FAILED', `Failed to submit review: ${result.error.message}`);
      }

      // Update app's average rating
      await this.updateAppRating(appId, tenantUuid);

      return {
        data: result.data,
        message: existingReview ? 'Review updated successfully' : 'Review submitted successfully'
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('REVIEW_SUBMISSION_FAILED', `Review submission failed: ${error.message}`);
    }
  }

  // Get reviews for a specific app
  async getAppReviews(appId, tenantId, options = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const {
        limit = 20,
        offset = 0,
        sortBy = 'review_date',
        sortOrder = 'desc',
        rating = null,
        verifiedOnly = false
      } = options;

      let queryBuilder = supabase
        .from(this.reviewsTable)
        .select(`
          *,
          user:users!inner(
            id,
            email,
            display_name,
            avatar_url
          )
        `)
        .eq('app_id', appId)
        .eq('tenant_id', tenantUuid);

      // Filter by rating
      if (rating) {
        queryBuilder = queryBuilder.eq('rating', rating);
      }

      // Filter by verified users only
      if (verifiedOnly) {
        queryBuilder = queryBuilder.eq('is_verified_user', true);
      }

      // Sorting
      queryBuilder = queryBuilder.order(sortBy, { ascending: sortOrder === 'asc' });

      // Pagination
      queryBuilder = queryBuilder.range(offset, offset + limit - 1);

      const { data, error, count } = await queryBuilder;

      if (error) {
        throw new ServiceError('REVIEWS_FETCH_FAILED', `Failed to fetch reviews: ${error.message}`);
      }

      return {
        data: data || [],
        count: count || 0,
        pagination: {
          limit,
          offset,
          total: count || 0
        }
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('REVIEWS_FETCH_FAILED', `Failed to fetch reviews: ${error.message}`);
    }
  }

  // Get user's reviews
  async getUserReviews(userId, tenantId, options = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const {
        limit = 20,
        offset = 0,
        sortBy = 'review_date',
        sortOrder = 'desc'
      } = options;

      const { data, error, count } = await supabase
        .from(this.reviewsTable)
        .select(`
          *,
          app:apps!inner(
            id,
            app_name,
            app_description,
            app_icon_url,
            app_category
          )
        `)
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(offset, offset + limit - 1);

      if (error) {
        throw new ServiceError('USER_REVIEWS_FETCH_FAILED', `Failed to fetch user reviews: ${error.message}`);
      }

      return {
        data: data || [],
        count: count || 0,
        pagination: {
          limit,
          offset,
          total: count || 0
        }
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('USER_REVIEWS_FETCH_FAILED', `Failed to fetch user reviews: ${error.message}`);
    }
  }

  // Mark a review as helpful
  async markReviewHelpful(reviewId, userId, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .update({
          helpful_count: supabase.sql`helpful_count + 1`
        })
        .eq('id', reviewId)
        .eq('tenant_id', tenantUuid)
        .select()
        .single();

      if (error) {
        throw new ServiceError('HELPFUL_MARK_FAILED', `Failed to mark review as helpful: ${error.message}`);
      }

      return {
        data,
        message: 'Review marked as helpful'
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('HELPFUL_MARK_FAILED', `Failed to mark review as helpful: ${error.message}`);
    }
  }

  // Delete a user's review
  async deleteReview(reviewId, userId, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Verify the review belongs to the user
      const { data: review } = await supabase
        .from(this.reviewsTable)
        .select('app_id')
        .eq('id', reviewId)
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .single();

      if (!review) {
        throw new ServiceError('REVIEW_NOT_FOUND', 'Review not found or you do not have permission to delete it');
      }

      const { error } = await supabase
        .from(this.reviewsTable)
        .delete()
        .eq('id', reviewId)
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid);

      if (error) {
        throw new ServiceError('REVIEW_DELETE_FAILED', `Failed to delete review: ${error.message}`);
      }

      // Update app's average rating
      await this.updateAppRating(review.app_id, tenantUuid);

      return {
        message: 'Review deleted successfully'
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('REVIEW_DELETE_FAILED', `Failed to delete review: ${error.message}`);
    }
  }

  // Get review statistics for an app
  async getAppReviewStats(appId, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .select('rating')
        .eq('app_id', appId)
        .eq('tenant_id', tenantUuid);

      if (error) {
        throw new ServiceError('REVIEW_STATS_FAILED', `Failed to get review statistics: ${error.message}`);
      }

      const ratings = data || [];
      const totalReviews = ratings.length;
      
      if (totalReviews === 0) {
        return {
          totalReviews: 0,
          averageRating: 0,
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
      }

      const averageRating = ratings.reduce((sum, review) => sum + review.rating, 0) / totalReviews;
      
      const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      ratings.forEach(review => {
        ratingDistribution[review.rating]++;
      });

      return {
        totalReviews,
        averageRating: Math.round(averageRating * 10) / 10,
        ratingDistribution
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('REVIEW_STATS_FAILED', `Failed to get review statistics: ${error.message}`);
    }
  }

  // Update app's average rating (called after review changes)
  async updateAppRating(appId, tenantUuid) {
    try {
      const stats = await this.getAppReviewStats(appId, tenantUuid);
      
      const { error } = await supabase
        .from(this.appsTable)
        .update({
          rating: stats.averageRating,
          total_reviews: stats.totalReviews,
          updated_at: new Date().toISOString()
        })
        .eq('id', appId)
        .eq('tenant_id', tenantUuid);

      if (error) {
        console.error('Failed to update app rating:', error);
      }
    } catch (error) {
      console.error('Failed to update app rating:', error);
    }
  }

  // Get top reviews (most helpful)
  async getTopReviews(tenantId, limit = 10) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.reviewsTable)
        .select(`
          *,
          user:users!inner(
            id,
            email,
            display_name,
            avatar_url
          ),
          app:apps!inner(
            id,
            app_name,
            app_description,
            app_icon_url
          )
        `)
        .eq('tenant_id', tenantUuid)
        .gte('helpful_count', 5)
        .order('helpful_count', { ascending: false })
        .limit(limit);

      if (error) {
        throw new ServiceError('TOP_REVIEWS_FAILED', `Failed to get top reviews: ${error.message}`);
      }

      return {
        data: data || []
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('TOP_REVIEWS_FAILED', `Failed to get top reviews: ${error.message}`);
    }
  }
}

export const appUserReviewService = new AppUserReviewService();
